<script>

    import app from '../data/app.svelte.js';

    const direction = $derived(app.state.direction === 'right' ? 1 : app.state.direction === 'left' ? -1 : 0);

    import Title from './Title.svelte'

   	import { fly, fade } from 'svelte/transition';

    let { children, title = null, navigation = null, back = null, fill = false, icon = null } = $props();

</script>

<section class:fill>
    <contents transition:fade>
        <Title {title} {icon} />

        {#if back}
            <div>
                {@render back?.()}
            </div>
        {/if}

        <div in:fly={{ x: direction * 200, duration: 200 }} out:fly={{ x: direction * -200, duration: 200 }}>
            {@render children?.()}
        </div>
    </contents>

    {#if navigation}
        <footer transition:fade>
            {@render navigation?.()}
        </footer>
    {/if}
</section>

<style>

    section {
        grid-row: 2;
        grid-column: 1;

        display: grid;
        grid-template-rows: 1fr min-content;
        min-height: 100%;

        background: var(--background-color);
        border-radius: var(--card-radius);
    }

    /* Opt-in: fill the content area (definite height) instead of sizing to content,
       so the footer pins to the very bottom — used by the inline chat. min-height: 0
       lets the scrollable contents shrink to the track and scroll internally. */
    section.fill {
        height: 100%;
    }

    section.fill > contents {
        min-height: 0;
    }

    section > contents {
        grid-row: 1;
        padding: 0 3em 3em 3em;

        overflow-x: hidden;
        overflow-y: scroll;
    }

    @supports selector(::-webkit-scrollbar) {
        section > contents {
            padding-right: 1.8em;
            border-right: 0.6em solid transparent;
        }

        section > contents::-webkit-scrollbar { 
            width: 0.6em; 
            height: 0.6em; 
        }

        section > contents::-webkit-scrollbar-track-piece { 
            margin-top: 0.6em;
            margin-bottom: 0;
            background-color: transparent; 
        }

        section > contents::-webkit-scrollbar-thumb:vertical { 
            border-radius: 4px;
            background-color: #ddd;
        }

        @media (prefers-color-scheme: dark) {
            section > contents::-webkit-scrollbar-thumb:vertical { 
                background-color: #555;
            }
        }
    }

    @supports (not selector(::-webkit-scrollbar)) and (scrollbar-color: auto) {
        section > contents {
            scrollbar-color: #ddd transparent;
            padding-right: calc(3em - var(--scrollbar-width, 0px));
        }

        @media (prefers-color-scheme: dark) {
            section > contents {
                scrollbar-color: #555 transparent;
            }
        }
    }

    section > contents:has(+ footer) {
        padding-bottom: 0;
    }

    section > footer {
        grid-row: 2;
        grid-column: 1;
        padding: 0 3em 3em;
       
        border-bottom-left-radius: var(--card-radius);
        border-bottom-right-radius: var(--card-radius);
    }

    section.fill > footer {
        padding-top: 1em;
        padding-bottom: 2em;
    }



    @media (prefers-color-scheme: dark) {
        section > contents::-webkit-scrollbar-thumb:vertical { 
            background-color: #555;
        }
    }

    @media (max-width: 600px) {
        section {
            grid-template-rows: min-content min-content;
            min-height: auto;

            padding-bottom: 2em;
            border-radius: 0;
        }

        section > contents {
            overflow: visible;

            padding-left: 2em;
            padding-right: 2em;
            padding-bottom: 0;

            border-right: none;
        }

        section > footer {
            padding-left: 2em;
            padding-right: 2em;
        }

        /* Single page-scroll chat on mobile: lay the section out as a flex column so the
           composer (footer) sits at the bottom and the message list can be pushed down to
           it when the conversation is short. When the messages outgrow the viewport the
           column grows and the page scrolls — no internal scroller. */
        section.fill {
            display: flex;
            flex-direction: column;
            height: auto;
        }

        section.fill > contents {
            flex: 1 0 auto;
            display: flex;
            flex-direction: column;
            overflow: visible;
            min-height: 0;
        }

        /* Push the chat (the fly-wrapped last child) down to the bottom of the content
           area when there's spare room; collapses to 0 once the messages overflow. */
        section.fill > contents > :last-child {
            margin-top: auto;
        }

        section.fill > footer {
            flex: 0 0 auto;
        }
    }

    /* Drop the bottom padding so the composer is flush with the screen edge ONLY on a
       real touch device — not on a narrow desktop window (small viewport, but a mouse),
       where the composer should keep its normal bottom gap. */
    @media (max-width: 600px) and (pointer: coarse) {
        section.fill,
        section.fill > footer {
            padding-bottom: 0;
        }
    }


</style>